import { AnimatePresence, motion } from 'framer-motion';
import { type IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { type Campaign, type Targeting } from '@feathr/blackbox';
import { CardV2 as Card, Slider, Toggle, Tooltip } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';
import { useAccount, useUser } from '@feathr/report_components';

import { useLookalikeAudience } from './LookalikeAudienceTargeting.hooks';
import {
  getSliderMarks,
  onFormatTickLabel,
  roundValue,
  SLIDER_MAX_VALUE,
  SLIDER_MIN_VALUE,
  sliderToValue,
  valueToSlider,
} from './LookalikeAudienceTargeting.utils';

const MIN_REACHABLE_USERS = 5_000;
const MIN_REACHABLE_USERS_INTERNAL = 100;

interface IProps {
  numReachableUsers: number;
  isLoading: boolean;
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

/** Lookalike Audience Targeting for Pixl Plus campaigns */
function LookalikeAudienceTargeting({
  numReachableUsers,
  isLoading,
  campaign,
  targetings,
}: Readonly<IProps>): JSX.Element {
  const { isInternal } = useUser();
  const { t } = useTranslation();
  const { isPlatformLite } = useAccount();

  // Allow internal users to target with less reachable users for testing purposes
  const minReachableUsers = isInternal ? MIN_REACHABLE_USERS_INTERNAL : MIN_REACHABLE_USERS;

  const { handleSliderChange, handleToggleChange, isEnabled, audienceSize, hasSpecialAdCategory } =
    useLookalikeAudience({
      targetings,
      campaign,
      numReachableUsers,
      minReachableUsers,
      isLoading,
    });

  const hasEnoughReachableUsers = numReachableUsers >= minReachableUsers;
  const isDisabled = isPlatformLite || hasSpecialAdCategory || !hasEnoughReachableUsers;
  const sliderMarks = getSliderMarks(t);
  const shouldShowSlider = isEnabled && !isDisabled;

  const handleSliderChangeWithConversion = (sliderPosition: number): void => {
    const actualValue = sliderToValue(sliderPosition);
    const roundedValue = roundValue(actualValue);
    handleSliderChange(roundedValue);
  };

  function getDisabledMessage(): string {
    if (isPlatformLite) {
      return t('Lookalike targeting is only available on Essential and Advanced plans.');
    }
    if (hasSpecialAdCategory) {
      return t("Lookalike targeting isn't available when a Meta special ad category is set.");
    }
    return t('Lookalike targeting requires {{minReachableUsers}} reachable people.', {
      minReachableUsers: numeral(MIN_REACHABLE_USERS).format('0,0'),
    });
  }

  const disabledMessage = getDisabledMessage();

  return (
    <Card disabled={isDisabled} name={'lookalike_audience_targeting_card'} width={'full'}>
      <Card.Header
        description={t(
          'Expand your reach by targeting people who are similar to your targeted groups.',
        )}
        title={t('Lookalike Audience Targeting')}
      >
        {isDisabled ? (
          <Tooltip title={disabledMessage}>{getIconForAction('locked')}</Tooltip>
        ) : (
          <Toggle
            aria-label={t('Lookalike audience targeting')}
            onChange={handleToggleChange}
            value={isEnabled}
          />
        )}
      </Card.Header>

      <AnimatePresence>
        {shouldShowSlider && (
          <motion.div
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
          >
            <Card.Content isLoading={isLoading}>
              <Slider
                helpText={t(
                  "Use the slider below to adjust the relative size of the audience you'd like to target. It could take up to 7 days for the ads to start serving impressions.",
                )}
                label={t('Audience Size')}
                labelAlwaysOn={true}
                marks={sliderMarks}
                max={SLIDER_MAX_VALUE}
                min={SLIDER_MIN_VALUE}
                onChange={handleSliderChangeWithConversion}
                preventLabelOverflow={true}
                tickLabel={onFormatTickLabel}
                value={valueToSlider(audienceSize ?? 10_000)}
              />
            </Card.Content>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

export default observer(LookalikeAudienceTargeting);
